const CHUNK_SIZE = 16;

function keyFromChunk(cx, cz) {
  return `${cx},${cz}`;
}

function touchLru(cache, key, value) {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
}

export function createChunkColumnWorkerClient({
  enabled = true,
  maxCacheChunks = 72,
  maxInFlight = 2,
  worldSeed = 0
} = {}) {
  const canUseWorker = enabled
    && typeof Worker !== 'undefined'
    && typeof URL !== 'undefined';

  const cache = new Map();
  const pendingQueue = [];
  const pendingSet = new Set();
  const inFlight = new Set();
  const stats = {
    enabled: canUseWorker,
    requested: 0,
    completed: 0,
    hits: 0,
    misses: 0,
    failed: 0
  };
  let runtimeMaxCacheChunks = Math.max(24, Math.floor(Number(maxCacheChunks) || 72));
  let runtimeMaxInFlight = Math.max(1, Math.floor(Number(maxInFlight) || 2));
  let runtimeWorldSeed = Math.trunc(Number(worldSeed) || 0) | 0;
  const MIN_QUEUE_CAP = 48;
  const MAX_QUEUE_CAP = 720;

  let worker = null;

  function trimCache() {
    while (cache.size > runtimeMaxCacheChunks) {
      const oldest = cache.keys().next().value;
      if (oldest === undefined) break;
      cache.delete(oldest);
    }
  }

  function trimPendingQueue() {
    const maxQueued = Math.min(
      MAX_QUEUE_CAP,
      Math.max(MIN_QUEUE_CAP, runtimeMaxCacheChunks * 2)
    );
    while (pendingQueue.length > maxQueued) {
      const dropped = pendingQueue.pop();
      if (!dropped) break;
      pendingSet.delete(keyFromChunk(dropped.cx, dropped.cz));
    }
  }

  function flushQueue() {
    if (!worker) return;
    while (pendingQueue.length > 0 && inFlight.size < runtimeMaxInFlight) {
      const next = pendingQueue.shift();
      if (!next) break;
      const key = keyFromChunk(next.cx, next.cz);
      pendingSet.delete(key);
      if (cache.has(key) || inFlight.has(key)) continue;
      inFlight.add(key);
      stats.requested += 1;
      worker.postMessage({
        type: 'prefetch_chunk',
        cx: next.cx,
        cz: next.cz
      });
    }
  }

  function enqueueChunk(cx, cz) {
    const key = keyFromChunk(cx, cz);
    if (cache.has(key) || pendingSet.has(key) || inFlight.has(key)) return;
    pendingQueue.push({ cx, cz });
    pendingSet.add(key);
  }

  function prefetchNeighborhood(cx, cz, radius = 2) {
    if (!worker) return;
    const jobs = [];
    for (let dz = -radius; dz <= radius; dz++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const qx = cx + dx;
        const qz = cz + dz;
        jobs.push({
          cx: qx,
          cz: qz,
          distSq: (dx * dx) + (dz * dz)
        });
      }
    }
    jobs.sort((a, b) => a.distSq - b.distSq);
    for (const job of jobs) enqueueChunk(job.cx, job.cz);
    trimPendingQueue();
    flushQueue();
  }

  function getColumnInfo(x, z) {
    const cx = Math.floor(x / CHUNK_SIZE);
    const cz = Math.floor(z / CHUNK_SIZE);
    const key = keyFromChunk(cx, cz);
    const entry = cache.get(key);
    if (!entry) {
      stats.misses += 1;
      return null;
    }

    touchLru(cache, key, entry);

    const lx = ((x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const lz = ((z % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const idx = (lz * CHUNK_SIZE) + lx;
    stats.hits += 1;
    return entry.columns[idx] || null;
  }

  function clear() {
    cache.clear();
    pendingQueue.length = 0;
    pendingSet.clear();
    inFlight.clear();
  }

  function setRuntimeTuning({ maxCacheChunks: nextCache, maxInFlight: nextInFlight } = {}) {
    const parsedCache = Math.floor(Number(nextCache));
    if (Number.isFinite(parsedCache) && parsedCache >= 24) {
      runtimeMaxCacheChunks = parsedCache;
      trimCache();
      trimPendingQueue();
    }

    const parsedInFlight = Math.floor(Number(nextInFlight));
    if (Number.isFinite(parsedInFlight) && parsedInFlight >= 1) {
      runtimeMaxInFlight = parsedInFlight;
    }

    flushQueue();
  }

  function getStats() {
    return {
      ...stats,
      worldSeed: runtimeWorldSeed,
      cachedChunks: cache.size,
      queuedChunks: pendingQueue.length,
      inflightChunks: inFlight.size,
      maxCacheChunks: runtimeMaxCacheChunks,
      maxInFlight: runtimeMaxInFlight
    };
  }

  function setWorldSeed(nextSeed) {
    const parsed = Number(nextSeed);
    if (!Number.isFinite(parsed)) return false;
    runtimeWorldSeed = Math.trunc(parsed) | 0;
    clear();
    if (worker) {
      worker.postMessage({
        type: 'set_world_seed',
        worldSeed: runtimeWorldSeed
      });
    }
    return true;
  }

  if (canUseWorker) {
    try {
      worker = new Worker(new URL('../workers/chunk-column-worker.js', import.meta.url), { type: 'module' });
      worker.postMessage({
        type: 'set_world_seed',
        worldSeed: runtimeWorldSeed
      });
      worker.addEventListener('message', (event) => {
        const message = event?.data;
        if (!message || typeof message !== 'object') return;
        if (message.type === 'prefetch_chunk_result') {
          const cx = Number(message.cx);
          const cz = Number(message.cz);
          const columns = Array.isArray(message.columns) ? message.columns : [];
          const key = keyFromChunk(cx, cz);
          inFlight.delete(key);
          if (columns.length === CHUNK_SIZE * CHUNK_SIZE) {
            touchLru(cache, key, { columns });
            trimCache();
            stats.completed += 1;
          } else {
            stats.failed += 1;
          }
          flushQueue();
        } else if (message.type === 'prefetch_chunk_error') {
          const key = keyFromChunk(Number(message.cx), Number(message.cz));
          inFlight.delete(key);
          stats.failed += 1;
          flushQueue();
        }
      });

      worker.addEventListener('error', () => {
        stats.failed += 1;
      });
    } catch (err) {
      console.warn('[chunk-column-worker] failed to initialize, using main thread fallback', err);
      worker = null;
      stats.enabled = false;
    }
  }

  return {
    isEnabled() {
      return !!worker;
    },
    prefetchNeighborhood,
    getColumnInfo,
    setRuntimeTuning,
    setWorldSeed,
    clear,
    getStats
  };
}
